/**
 * Social Service DB - Database-backed social features service
 * Replaces localStorage-based socialService.js with backend API calls
 * Uses the existing backend social endpoints at /api/social
 */
import { API_BASE } from '../config/api';
import AuthService from './authService';

class SocialServiceDB {
  static API_BASE = API_BASE;

  // Use centralized AuthService.handleResponse for auth error handling

  // ========================
  // TRIP LIKES (uses content_interactions table)
  // ========================

  static async likeTrip(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType: 'trip',
          contentId: tripId,
          interactionType: 'like'
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error liking trip:', error);
      return { success: false, error: error.message };
    }
  }

  static async unlikeTrip(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions?contentType=trip&contentId=${tripId}&interactionType=like`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error unliking trip:', error);
      return { success: false, error: error.message };
    }
  }

  static async getTripLikes(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions/content/trip/${tripId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.stats || {};
    } catch (error) {
      console.error('Error getting trip likes:', error);
      return {};
    }
  }

  static async getTripLikeCount(tripId) {
    const likes = await this.getTripLikes(tripId);
    return likes.like_count || 0;
  }

  static async isLikedByUser(tripId, userId = null) {
    try {
      const likes = await this.getTripLikes(tripId);
      return likes.user_has_liked || false;
    } catch (error) {
      console.error('Error checking if liked:', error);
      return false;
    }
  }

  // Activity likes (same pattern as trips)
  static async likeActivity(activityId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType: 'activity',
          contentId: activityId,
          interactionType: 'like'
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error liking activity:', error);
      return { success: false, error: error.message };
    }
  }

  static async unlikeActivity(activityId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions?contentType=activity&contentId=${activityId}&interactionType=like`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error unliking activity:', error);
      return { success: false, error: error.message };
    }
  }

  static async getActivityLikeCount(activityId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions/content/activity/${activityId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.stats?.like_count || 0;
    } catch (error) {
      console.error('Error getting activity likes:', error);
      return 0;
    }
  }

  static async isActivityLikedByUser(activityId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions/content/activity/${activityId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.stats?.user_has_liked || false;
    } catch (error) {
      console.error('Error checking if activity liked:', error);
      return false;
    }
  }

  // ========================
  // GENERIC CONTENT LIKES (supports any content type)
  // ========================

  /**
   * Like any content type (trip, activity, collection, etc.)
   * @param {string} contentType - Type of content ('trip', 'activity', 'collection', etc.)
   * @param {string} contentId - ID of the content to like
   */
  static async likeContent(contentType, contentId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType,
          contentId,
          interactionType: 'like'
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error(`Error liking ${contentType}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Unlike any content type (trip, activity, collection, etc.)
   * @param {string} contentType - Type of content ('trip', 'activity', 'collection', etc.)
   * @param {string} contentId - ID of the content to unlike
   */
  static async unlikeContent(contentType, contentId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions?contentType=${contentType}&contentId=${contentId}&interactionType=like`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error(`Error unliking ${contentType}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get like stats for any content type
   * @param {string} contentType - Type of content ('trip', 'activity', 'collection', etc.)
   * @param {string} contentId - ID of the content
   */
  static async getContentLikeStats(contentType, contentId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/interactions/content/${contentType}/${contentId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.stats || {};
    } catch (error) {
      console.error(`Error getting ${contentType} likes:`, error);
      return {};
    }
  }

  // ========================
  // COMMENTS (uses content_comments table)
  // ========================

  static async addComment(tripId, text) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/comments`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType: 'trip',
          contentId: tripId,
          text
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error adding comment:', error);
      return { success: false, error: error.message };
    }
  }

  static async getTripComments(tripId = null) {
    if (!tripId) return [];

    try {
      const response = await fetch(`${this.API_BASE}/api/social/comments/trip/${tripId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.comments || [];
    } catch (error) {
      console.error('Error getting comments:', error);
      return [];
    }
  }

  static async deleteComment(commentId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/comments/${commentId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error deleting comment:', error);
      return { success: false, error: error.message };
    }
  }

  // ========================
  // FOLLOWING (uses user_follows table)
  // ========================

  static async followUser(userId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/follows`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ userId })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error following user:', error);
      return { success: false, error: error.message };
    }
  }

  static async unfollowUser(userId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/follows/${userId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error unfollowing user:', error);
      return { success: false, error: error.message };
    }
  }

  static async getFollowers() {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/follows/followers`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.followers || [];
    } catch (error) {
      console.error('Error getting followers:', error);
      return [];
    }
  }

  static async getFollowing() {
    try {
      const response = await fetch(`${this.API_BASE}/api/social/follows/following`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.following || [];
    } catch (error) {
      console.error('Error getting following:', error);
      return [];
    }
  }

  static async isFollowing(userId) {
    try {
      const following = await this.getFollowing();
      return following.some(user => String(user.id) === String(userId));
    } catch (error) {
      console.error('Error checking following status:', error);
      return false;
    }
  }

  // ========================
  // SOCIAL FEED (uses trips and interactions)
  // ========================

  static async getSocialFeed(filters = {}) {
    try {
      const { feedFilter = 'all', limit = 50, offset = 0 } = filters;

      const params = new URLSearchParams({
        feedFilter,
        limit: limit.toString(),
        offset: offset.toString()
      });

      const response = await fetch(`${this.API_BASE}/api/trips/feed?${params}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.trips || [];
    } catch (error) {
      console.error('Error getting social feed:', error);
      return [];
    }
  }

  // ========================
  // HELPER METHODS
  // ========================

  static async likeItem(itemId, itemType) {
    if (itemType === 'trip') {
      return this.likeTrip(itemId);
    } else if (itemType === 'activity') {
      return this.likeActivity(itemId);
    }
    return { success: false, error: 'Invalid item type' };
  }

  static async unlikeItem(itemId, itemType) {
    if (itemType === 'trip') {
      return this.unlikeTrip(itemId);
    } else if (itemType === 'activity') {
      return this.unlikeActivity(itemId);
    }
    return { success: false, error: 'Invalid item type' };
  }

  // ========================
  // TRIP MIGRATION (localStorage → database)
  // ========================

  /**
   * Migrate trips from localStorage to database
   * One-time migration to move existing trips to the database
   * @returns {object} Migration result with success count
   */
  static async migrateTripsToDatabase() {
    try {
      const trips = JSON.parse(localStorage.getItem('trips') || '[]');

      if (trips.length === 0) {
        return { success: true, message: 'No trips to migrate', migratedCount: 0 };
      }

      console.log(`🔄 Starting migration of ${trips.length} trips from localStorage to database...`);

      const results = {
        total: trips.length,
        migrated: 0,
        skipped: 0,
        errors: []
      };

      for (const trip of trips) {
        try {
          // Map localStorage trip to database trip structure
          const tripData = {
            name: trip.name,
            description: trip.description || '',
            startDate: trip.startDate,
            endDate: trip.endDate,
            location: trip.location || '',
            tripType: trip.tripType || 'leisure',
            status: trip.status || 'planning',
            visibility: trip.shareSettings?.visibility || 'private',
            allowComments: trip.shareSettings?.allowComments ?? true,
            allowLikes: trip.shareSettings?.allowLikes ?? true
          };

          // Create trip in database
          const response = await fetch(`${this.API_BASE}/api/trips`, {
            method: 'POST',
            headers: AuthService.getJsonHeaders(),
            body: JSON.stringify(tripData)
          });

          if (response.ok) {
            const result = await AuthService.handleResponse(response);
            console.log(`✅ Migrated trip: ${trip.name}`);
            results.migrated++;

            // If trip was already shared, update visibility
            if (trip.shareSettings?.visibility && trip.shareSettings.visibility !== 'private') {
              await this.updateTripSharing(result.trip.id, {
                visibility: trip.shareSettings.visibility,
                allowComments: trip.shareSettings.allowComments ?? true,
                allowLikes: trip.shareSettings.allowLikes ?? true
              });
            }
          } else {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.warn(`⚠️ Skipped trip ${trip.name}: ${error.error}`);
            results.skipped++;
            results.errors.push({ tripName: trip.name, error: error.error });
          }
        } catch (error) {
          console.error(`❌ Error migrating trip ${trip.name}:`, error);
          results.errors.push({ tripName: trip.name, error: error.message });
        }
      }

      console.log(`✅ Migration complete: ${results.migrated} migrated, ${results.skipped} skipped, ${results.errors.length} errors`);

      return {
        success: true,
        message: `Migration complete! ${results.migrated}/${results.total} trips migrated`,
        ...results
      };
    } catch (error) {
      console.error('❌ Migration failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get trip sharing settings
   * @param {string} tripId - Trip ID
   * @returns {object} Result with sharing data
   */
  static async getTripSharing(tripId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/trips/${tripId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      const trip = data.trip || data;
      return {
        success: true,
        data: {
          visibility: trip.visibility || 'private',
          allow_comments: trip.allow_comments ?? true,
          allow_likes: trip.allow_likes ?? true,
          // best_for_tags comes from getTripById (the only query that returns it,
          // already parsed to an array). Surfaced here so SharingModal seeds the
          // editor from the authoritative value — card/feed items don't carry it,
          // and seeding from those would let the first toggle overwrite saved tags.
          best_for_tags: Array.isArray(trip.best_for_tags) ? trip.best_for_tags : []
        }
      };
    } catch (error) {
      console.error('Error getting trip sharing:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get activity sharing settings
   * @param {string} activityId - Activity ID
   * @returns {object} Result with sharing data
   */
  static async getActivitySharing(activityId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/activity-instances/${activityId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      const activity = data.activity || data;
      return {
        success: true,
        data: {
          visibility: activity.visibility || 'private',
          allow_comments: activity.allow_comments ?? true,
          allow_likes: activity.allow_likes ?? true
        }
      };
    } catch (error) {
      console.error('Error getting activity sharing:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update trip sharing settings (visibility, comments, likes)
   * @param {string} tripId - Trip ID
   * @param {object} shareSettings - Sharing settings
   * @returns {object} Result
   */
  static async updateTripSharing(tripId, shareSettings) {
    try {
      const response = await fetch(`${this.API_BASE}/api/trips/${tripId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          visibility: shareSettings.visibility,
          allow_comments: shareSettings.allowComments ?? true,
          allow_likes: shareSettings.allowLikes ?? true
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating trip sharing:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update activity sharing settings
   * @param {string} activityId - Activity ID
   * @param {object} shareSettings - Sharing settings
   * @returns {object} Result
   */
  static async updateActivitySharing(activityId, shareSettings) {
    try {
      const response = await fetch(`${this.API_BASE}/api/activity-instances/${activityId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          visibility: shareSettings.visibility,
          allow_comments: shareSettings.allowComments ?? true,
          allow_likes: shareSettings.allowLikes ?? true,
          shared_at: shareSettings.visibility !== 'private' ? new Date().toISOString() : null
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating activity sharing:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Share an activity with specified settings
   * @param {string} activityId - Activity ID
   * @param {object} settings - Share settings
   * @returns {object} Result
   */
  static async shareActivity(activityId, settings) {
    try {
      const response = await fetch(`${this.API_BASE}/api/activity-instances/${activityId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          visibility: settings.visibility || 'friends',
          allow_comments: settings.allowComments ?? true,
          allow_likes: settings.allowLikes ?? true,
          shared_at: new Date().toISOString()
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error sharing activity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Unshare an activity (make it private)
   * @param {string} activityId - Activity ID
   * @returns {object} Result
   */
  static async unshareActivity(activityId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/activity-instances/${activityId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          visibility: 'private',
          shared_at: null
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error unsharing activity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get collection sharing settings
   * @param {string} collectionId - Collection ID
   * @returns {object} Result with sharing data
   */
  static async getCollectionSharing(collectionId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/collections/${collectionId}/sharing`, {
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error getting collection sharing:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update collection sharing settings (visibility, comments, likes)
   * @param {string} collectionId - Collection ID
   * @param {object} shareSettings - Sharing settings
   * @returns {object} Result
   */
  static async updateCollectionSharing(collectionId, shareSettings) {
    try {
      const response = await fetch(`${this.API_BASE}/api/collections/${collectionId}/sharing`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          visibility: shareSettings.visibility,
          allowComments: shareSettings.allowComments ?? true,
          allowLikes: shareSettings.allowLikes ?? true
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating collection sharing:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's trips (both public and private)
   * @returns {array} User's trips
   */
  static async getUserTrips() {
    try {
      const response = await fetch(`${this.API_BASE}/api/trips/user/me`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.trips || [];
    } catch (error) {
      console.error('Error getting user trips:', error);
      return [];
    }
  }

  // ========================
  // COLLECTION PREVIEW METHODS
  // ========================

  /**
   * Get collection preview with first 6 activities for carousel
   * @param {string} collectionId - Collection ID
   * @returns {object} Collection metadata + preview activities
   */
  static async getCollectionPreview(collectionId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/collections/${collectionId}/preview`, {
        headers: AuthService.getJsonHeaders()
      });
      // Missing, private, friends-only to a non-friend and moderated all
      // answer 404 alike; the page shows its not-found state for it
      if (response.status === 404) return { success: false, notFound: true };

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error getting collection preview:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get paginated activities for a collection (for Preview Page)
   * @param {string} collectionId - Collection ID
   * @param {number} limit - Number of activities per page (default 12)
   * @param {number} offset - Offset for pagination (default 0)
   * @returns {object} Paginated activities
   */
  static async getCollectionActivities(collectionId, limit = 12, offset = 0) {
    try {
      const response = await fetch(
        `${this.API_BASE}/api/collections/${collectionId}/activities-page?limit=${limit}&offset=${offset}`,
        { headers: AuthService.getJsonHeaders() }
      );

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error getting collection activities:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save collection reference to user's boards (bookmark another user's collection)
   * @param {string} collectionId - Collection ID to save
   * @returns {object} Result
   */
  static async saveCollectionToBoards(collectionId) {
    try {
      const response = await fetch(`${this.API_BASE}/api/collections/${collectionId}/save`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error saving collection:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Copy collection to user's own collections (full duplicate)
   * @param {string} collectionId - Collection ID to copy
   * @param {string} newName - Optional new name for the copied collection
   * @returns {object} Result with new collection ID
   */
  static async copyCollection(collectionId, newName = null) {
    try {
      const response = await fetch(`${this.API_BASE}/api/collections/${collectionId}/copy`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ name: newName })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error copying collection:', error);
      return { success: false, error: error.message };
    }
  }

  // ========================
  // PLACE DETAILS (for PlaceDetail page)
  // ========================

  /**
   * Get place/event details for the PlaceDetail page
   * Handles both Google Places (ChIJ...) and Ticketmaster events (tm_...)
   */
  static async getPlaceDetails(placeId) {
    try {
      const response = await fetch(
        `${this.API_BASE}/api/social/places/${encodeURIComponent(placeId)}/details`,
        { headers: AuthService.getJsonHeaders() }
      );

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error getting place details:', error);
      return { success: false, error: error.message };
    }
  }

  // ========================
  // REPORT CONTENT
  // ========================

  // options.reporterIsParent: "this is my child" on a child-safety report
  // about a photo (policy §8); the server ignores it anywhere else.
  static async reportContent(contentType, contentId, reportedUserId, reason, details = '', options = {}) {
    try {
      const response = await fetch(`${this.API_BASE}/api/reports`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType,
          contentId,
          reportedUserId: reportedUserId || null,
          reason,
          details,
          ...(options.reporterIsParent ? { reporterIsParent: true } : {})
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error reporting content:', error);
      return { success: false, error: error.message };
    }
  }
}

export default SocialServiceDB;
